import { useEffect, useMemo, useRef, useState, type CSSProperties, type FormEvent } from 'react';
import { Link, useParams, useSearchParams } from 'react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  CalendarPlus,
  Check,
  Clock,
  Hand,
  Info,
  MapPin,
  Phone,
  Sparkles,
  Users,
} from 'lucide-react';
import { OCCASIONS, tableFeatureLabel } from '@spacebooking/shared';
import { api, ApiError, errorMessage } from '../lib/api';
import { addDays, longDate, relativeDay, shortDate, time12 } from '../lib/dates';
import { classNames, inkFor, metres, plural, spelledReference } from '../lib/format';
import { useMediaQuery } from '../lib/hooks';
import type { FloorPlan, GuestBooking, PlanTable, PublicVenue, Slot, TableStatus } from '../lib/types';
import { FloorMap } from '../map/FloorMap';
import { nearestByKind, WayfindingList } from '../map/Wayfinding';
import { Field, Notice, Spinner, useTitle } from '../ui';
import { DateStrip, type DayInfo } from './DatePicker';
import { loadBookings, loadGuest, rememberBooking, saveGuest, type RememberedBooking } from './guestMemory';

interface VenueResponse {
  venue: PublicVenue;
  preview: boolean;
  today: string;
  hours_summary: string;
  floor_plans: FloorPlan[];
}

interface AvailabilityResponse {
  date: string;
  closed: boolean;
  note: string | null;
  services: Array<{ name: string; starts: string; ends: string }>;
  slots: Slot[];
}

interface TablesResponse {
  slot_available: boolean;
  ends_at_local: string;
  tables: TableStatus[];
  combinations: Array<{ combination_id: string; name: string; table_ids: string[]; available: boolean }>;
}

export function BookingPage() {
  const { slug = '' } = useParams();
  const venueQ = useQuery({
    queryKey: ['public-venue', slug],
    queryFn: () => api<VenueResponse>(`/api/public/venues/${encodeURIComponent(slug)}`),
    retry: (count, err) => !(err instanceof ApiError && err.status === 404) && count < 2,
  });
  if (venueQ.isPending) return <BookingSkeleton />;
  if (venueQ.isError) {
    return (
      <div className="bk-missing">
        <h1 className="display">We couldn't find that venue</h1>
        <p className="muted">{errorMessage(venueQ.error)}. Check the link, or browse <Link to="/">all venues</Link>.</p>
      </div>
    );
  }
  return <BookingFlow data={venueQ.data} slug={slug} />;
}

function BookingSkeleton() {
  return (
    <div className="bk">
      <header className="bk-hero" style={{ background: 'var(--sunken)' }}>
        <div className="bk-hero-inner">
          <div className="skeleton" style={{ width: 120, height: 12 }} />
          <div className="skeleton" style={{ width: 320, height: 40, marginTop: 14 }} />
        </div>
      </header>
    </div>
  );
}

type Stage = 'choose' | 'details' | 'done';

function BookingFlow({ data, slug }: { data: VenueResponse; slug: string }) {
  const { venue } = data;
  const rules = venue.booking_rules;
  const [search] = useSearchParams();
  const embed = search.get('embed') === '1';
  const queryClient = useQueryClient();
  const isWide = useMediaQuery('(min-width: 1000px)');
  useTitle(`Book a table · ${venue.name}`);

  const remembered = useMemo(() => loadGuest(), []);
  const pastBookings = useMemo(() => loadBookings(slug), [slug]);
  const upcoming = pastBookings.filter((b) => b.date >= data.today).sort((a, b) => (a.date + a.time).localeCompare(b.date + b.time))[0];
  const lastBooking = pastBookings[0];

  const clampParty = (n: number) => Math.min(rules.max_party, Math.max(rules.min_party, n));
  const [party, setParty] = useState(() => clampParty(Number(search.get('party')) || 2));
  const [date, setDate] = useState(() => {
    const d = search.get('date');
    return d && d >= data.today ? d : data.today;
  });
  const [time, setTime] = useState<string | null>(search.get('time'));
  const [tableIds, setTableIds] = useState<string[]>([]);
  const [wantedTable, setWantedTable] = useState<string | null>(search.get('table'));
  const [planId, setPlanId] = useState(data.floor_plans[0]?.id ?? '');
  const [photo, setPhoto] = useState(false);
  const [activeRoute, setActiveRoute] = useState<string | null>(null);
  const [stage, setStage] = useState<Stage>('choose');
  const [flash, setFlash] = useState<{ text: string; tone: 'info' | 'warning' } | null>(null);
  const [result, setResult] = useState<{ booking: GuestBooking; token: string } | null>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const maxDate = addDays(data.today, rules.booking_window_days);

  const allTables = useMemo(() => data.floor_plans.flatMap((p) => p.tables), [data.floor_plans]);
  const tableById = useMemo(() => new Map(allTables.map((t) => [t.id, t])), [allTables]);
  const plan = data.floor_plans.find((p) => p.id === planId) ?? data.floor_plans[0];

  // --- Data ---------------------------------------------------------------
  const [monthsLoaded, setMonthsLoaded] = useState<string[]>([]);
  const calendarQ = useQuery({
    queryKey: ['calendar', slug, party, monthsLoaded],
    queryFn: async () => {
      const ranges = [{ from: data.today, days: 14 }, ...monthsLoaded.map((m) => ({ from: m < data.today ? data.today : m, days: 42 }))];
      const results = await Promise.all(
        ranges.map((r) => api<{ days: DayInfo[] }>(`/api/public/venues/${slug}/calendar`, { query: { from: r.from, days: r.days, party_size: party } })),
      );
      return new Map(results.flatMap((r) => r.days).map((d) => [d.date, d]));
    },
    placeholderData: (prev) => prev,
  });
  const availabilityQ = useQuery({
    queryKey: ['availability', slug, date, party],
    queryFn: () => api<AvailabilityResponse>(`/api/public/venues/${slug}/availability`, { query: { date, party_size: party } }),
    placeholderData: (prev) => (prev && prev.date === date ? prev : undefined),
  });
  const tablesQ = useQuery({
    queryKey: ['tables', slug, date, time, party],
    queryFn: () => api<TablesResponse>(`/api/public/venues/${slug}/tables`, { query: { date, time, party_size: party } }),
    enabled: Boolean(time),
  });

  const slots = availabilityQ.data?.slots ?? [];
  const states = useMemo(() => new Map((tablesQ.data?.tables ?? []).map((s) => [s.table_id, s])), [tablesQ.data]);

  // Drop a time that stopped being available (e.g. after changing party size).
  useEffect(() => {
    if (!time || !availabilityQ.data || availabilityQ.isPlaceholderData) return;
    const slot = availabilityQ.data.slots.find((s) => s.time === time);
    if (!slot?.available) setTime(null);
  }, [availabilityQ.data, availabilityQ.isPlaceholderData, time]);

  // Keep a chosen table only while it's still free; pick up a "wanted" one (book again).
  useEffect(() => {
    if (!tablesQ.data) return;
    if (tableIds.length && !tableIds.every((id) => states.get(id)?.state === 'available')) {
      setTableIds([]);
      setFlash({ text: 'Your table is taken at this time, so we have cleared it. Pick another or let us choose.', tone: 'warning' });
    }
    if (wantedTable && rules.allow_table_choice) {
      const st = states.get(wantedTable);
      if (st?.state === 'available') {
        setTableIds([wantedTable]);
        const t = tableById.get(wantedTable);
        if (t) setPlanId(data.floor_plans.find((p) => p.tables.some((x) => x.id === t.id))?.id ?? planId);
      } else if (st) {
        setFlash({ text: `Your usual table ${tableById.get(wantedTable)?.label ?? ''} isn't free then. We'll find you another good one.`, tone: 'info' });
      }
      setWantedTable(null);
    }
  }, [tablesQ.data]); // eslint-disable-line react-hooks/exhaustive-deps

  const selectedTables = tableIds.map((id) => tableById.get(id)).filter(Boolean) as PlanTable[];
  const routeTable = selectedTables.length === 1 ? selectedTables[0]! : null;

  // --- Interactions ---------------------------------------------------------
  const chooseParty = (n: number) => {
    setStage('choose');
    setParty(n);
    setTableIds([]);
    setFlash(null);
  };
  const chooseDate = (d: string) => {
    setStage('choose');
    setDate(d);
    setTime(null);
    setTableIds([]);
    setFlash(null);
  };
  const chooseTime = (t: string) => {
    setStage('choose');
    setTime(t);
    setFlash(null);
  };

  const onTableClick = (t: PlanTable) => {
    if (stage === 'done') return;
    if (!time) {
      setFlash({ text: 'Choose a time first, then tap a free table.', tone: 'info' });
      return;
    }
    const st = states.get(t.id);
    if (!st) return;
    if (st.state === 'available') {
      if (!rules.allow_table_choice) {
        setFlash({ text: `${venue.name} picks the best table for you, but this one is free at ${time12(time)}.`, tone: 'info' });
        return;
      }
      setTableIds(tableIds.length === 1 && tableIds[0] === t.id ? [] : [t.id]);
      setFlash(null);
      if (!isWide) document.getElementById('bk-table')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return;
    }
    const text =
      st.state === 'booked'
        ? `Table ${t.label} is booked${st.free_from ? ` until ${st.free_from}` : ''} — pick another table or time.`
        : st.state === 'too_small'
          ? `Table ${t.label} seats up to ${t.max_covers}. Choose a bigger table, or let us pick one for ${party}.`
          : st.state === 'too_large'
            ? `Table ${t.label} is for ${t.min_covers} or more, so we keep it for bigger groups.`
            : `Table ${t.label} is kept for walk-ins. Just pop in, or choose another table.`;
    setFlash({ text, tone: 'warning' });
  };

  const tableClass = (t: PlanTable) => {
    if (!time || !tablesQ.data) return 't-neutral';
    return `t-${states.get(t.id)?.state ?? 'neutral'}`;
  };

  const bookAgain = (b: RememberedBooking) => {
    setParty(clampParty(b.party_size));
    setTime(null);
    setTableIds([]);
    setWantedTable(b.table_ids[0] ?? null);
    setFlash({ text: `Pick a date and time — we'll try to give you table ${b.table_labels.join(' + ')} again.`, tone: 'info' });
  };

  const brandStyle = { '--brand': venue.brand_color, '--brand-ink': inkFor(venue.brand_color) } as CSSProperties;
  const groupedSlots = useMemo(() => {
    const groups: Array<{ service: string; slots: Slot[] }> = [];
    for (const s of slots) {
      const g = groups.find((x) => x.service === s.service);
      if (g) g.slots.push(s);
      else groups.push({ service: s.service, slots: [s] });
    }
    return groups;
  }, [slots]);
  const anyAvailable = slots.some((s) => s.available);
  const nextOpenDay = useMemo(() => {
    if (!calendarQ.data) return null;
    return [...calendarQ.data.values()].find((d) => d.date > date && d.available)?.date ?? null;
  }, [calendarQ.data, date]);

  const summaryLine = time ? `${plural(party, 'guest')} · ${relativeDay(date, data.today)} at ${time12(time)}` : null;

  // --- Render -----------------------------------------------------------------
  const top = (
    <div className="bk-top">
      {data.preview && <Notice tone="warning">Preview: this booking page isn't published yet. Bookings you make here are real.</Notice>}
      {upcoming && stage === 'choose' && (
        <div className="bk-memory">
          <div>
            <div className="eyebrow">Your booking</div>
            <div className="bk-memory-main">
              {longDate(upcoming.date)} at {time12(upcoming.time)} · {plural(upcoming.party_size, 'guest')}
            </div>
          </div>
          <Link className="btn btn-sm" to={`/booking/${upcoming.reference}?token=${encodeURIComponent(upcoming.token)}`}>
            Manage
          </Link>
        </div>
      )}
      {!upcoming && lastBooking && remembered && stage === 'choose' && (
        <div className="bk-memory">
          <div>
            <div className="eyebrow">Welcome back, {remembered.name.split(' ')[0]}</div>
            <div className="bk-memory-main">
              Last time: table {lastBooking.table_labels.join(' + ')} for {lastBooking.party_size}
            </div>
          </div>
          <button type="button" className="btn btn-sm" onClick={() => bookAgain(lastBooking)}>
            Book the same again
          </button>
        </div>
      )}

      <section className="bk-step" aria-labelledby="step-party">
        <h2 id="step-party" className="bk-step-title">
          <Users /> Guests
        </h2>
        <div className="party" role="group" aria-label="Number of guests">
          {Array.from({ length: Math.min(rules.max_party, 8) - rules.min_party + 1 }, (_, i) => i + rules.min_party).map((n) => (
            <button key={n} type="button" className="party-btn num" aria-pressed={party === n} onClick={() => chooseParty(n)}>
              {n}
            </button>
          ))}
          {rules.max_party > 8 && (
            <select
              className="select party-more"
              value={party > 8 ? party : ''}
              onChange={(e) => e.target.value && chooseParty(Number(e.target.value))}
              aria-label="More guests"
            >
              <option value="">9+</option>
              {Array.from({ length: rules.max_party - 8 }, (_, i) => i + 9).map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          )}
        </div>
        <p className="bk-help">
          More than {rules.max_party}?{' '}
          {venue.phone ? (
            <>
              Call us on <a href={`tel:${venue.phone.replace(/\s/g, '')}`}>{venue.phone}</a> and we'll sort you out.
            </>
          ) : (
            'Get in touch with the venue.'
          )}
        </p>
      </section>

      <section className="bk-step" aria-labelledby="step-date">
        <h2 id="step-date" className="bk-step-title">
          <CalendarPlus /> Date
        </h2>
        <DateStrip
          today={data.today}
          value={date}
          onChange={chooseDate}
          days={calendarQ.data ?? new Map()}
          maxDate={maxDate}
          loadMonth={(m) => setMonthsLoaded((list) => (list.includes(m) ? list : [...list, m]))}
        />
      </section>

      <section className="bk-step" aria-labelledby="step-time">
        <h2 id="step-time" className="bk-step-title">
          <Clock /> Time <span className="bk-step-sub">{longDate(date)}</span>
        </h2>
        {availabilityQ.isPending ? (
          <div className="slots">
            {Array.from({ length: 8 }, (_, i) => (
              <div key={i} className="skeleton" style={{ height: 40 }} />
            ))}
          </div>
        ) : availabilityQ.data?.closed ? (
          <Notice tone="info">
            Closed on {longDate(date)}
            {availabilityQ.data.note ? ` — ${availabilityQ.data.note}` : ''}.
            {nextOpenDay && (
              <>
                {' '}
                <button type="button" className="link-btn" onClick={() => chooseDate(nextOpenDay)}>
                  Try {relativeDay(nextOpenDay, data.today)}
                </button>
              </>
            )}
          </Notice>
        ) : (
          <>
            {!anyAvailable && (
              <Notice tone="warning">
                Fully booked for {plural(party, 'guest')} on {shortDate(date)}.
                {nextOpenDay && (
                  <>
                    {' '}
                    <button type="button" className="link-btn" onClick={() => chooseDate(nextOpenDay)}>
                      See {relativeDay(nextOpenDay, data.today)}
                    </button>
                  </>
                )}
              </Notice>
            )}
            {groupedSlots.map((g) => (
              <div key={g.service} className="slot-group">
                <div className="slot-service">{g.service}</div>
                <div className="slots" role="group" aria-label={`${g.service} times`}>
                  {g.slots.map((s) => (
                    <button
                      key={s.time}
                      type="button"
                      className="slot num"
                      aria-pressed={time === s.time}
                      disabled={!s.available}
                      onClick={() => chooseTime(s.time)}
                      title={!s.available ? reasonText(s.reason) : undefined}
                    >
                      {s.time}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </>
        )}
      </section>
    </div>
  );

  const tableSection = time && stage !== 'done' && (
    <section className="bk-step" id="bk-table" aria-labelledby="step-table">
      <h2 id="step-table" className="bk-step-title">
        <MapPin /> Table
      </h2>
      {flash && (
        <Notice tone={flash.tone === 'warning' ? 'warning' : 'info'} className="bk-flash">
          {flash.text}
        </Notice>
      )}
      {selectedTables.length > 0 ? (
        <div className="table-card">
          <div className="table-card-head">
            <div>
              <div className="table-card-name">
                {selectedTables.length > 1 ? `Tables ${selectedTables.map((t) => t.label).join(' + ')}` : `Table ${selectedTables[0]!.label}`}
              </div>
              <div className="table-card-sub">
                Seats {selectedTables.reduce((n, t) => n + t.min_covers, 0)}–{selectedTables.reduce((n, t) => n + t.max_covers, 0)}
                {' · '}
                {data.floor_plans.find((p) => p.tables.some((t) => t.id === selectedTables[0]!.id))?.name}
              </div>
            </div>
            {stage === 'choose' && (
              <button type="button" className="btn btn-sm btn-ghost" onClick={() => setTableIds([])}>
                Change
              </button>
            )}
          </div>
          {selectedTables.flatMap((t) => t.features).length > 0 && (
            <div className="row wrap gap-2" style={{ marginTop: 10 }}>
              {[...new Set(selectedTables.flatMap((t) => t.features))].map((f) => (
                <span key={f} className="badge badge-outline">
                  {tableFeatureLabel(f)}
                </span>
              ))}
            </div>
          )}
          {routeTable && routeTable.wayfinding.length > 0 && (
            <div className="table-card-way">
              <div className="eyebrow" style={{ marginBottom: 6 }}>
                Getting around from your table
              </div>
              <WayfindingList table={routeTable} active={activeRoute} onActive={setActiveRoute} />
            </div>
          )}
        </div>
      ) : (
        <div className="table-auto">
          <Sparkles />
          <div>
            <strong>We'll choose the best table</strong>
            <div className="muted">
              {rules.allow_table_choice ? 'Or tap a free table on the map to pick your own.' : `${venue.name} seats every party at the best table available.`}
            </div>
          </div>
        </div>
      )}
      {tablesQ.data && tablesQ.data.combinations.some((c) => c.available) && rules.allow_table_choice && (
        <div className="combos">
          <div className="eyebrow">Tables we can join for you</div>
          <div className="row wrap gap-2">
            {tablesQ.data.combinations
              .filter((c) => c.available)
              .map((c) => (
                <button
                  key={c.combination_id}
                  type="button"
                  className="chip"
                  aria-pressed={c.table_ids.length === tableIds.length && c.table_ids.every((id) => tableIds.includes(id))}
                  onClick={() => setTableIds(c.table_ids)}
                >
                  Tables {c.name}
                </button>
              ))}
          </div>
        </div>
      )}
      {stage === 'choose' && (
        <button type="button" className="btn btn-primary btn-lg btn-block bk-continue" onClick={() => setStage('details')}>
          Continue
        </button>
      )}
    </section>
  );

  const bottom = (
    <div className="bk-bottom" ref={panelRef}>
      {tableSection}
      {stage === 'details' && time && (
        <DetailsForm
          venue={venue}
          slug={slug}
          date={date}
          time={time}
          party={party}
          tableIds={tableIds}
          remembered={remembered}
          summary={summaryLine!}
          onBack={() => setStage('choose')}
          onConflict={(message) => {
            setStage('choose');
            setFlash({ text: message, tone: 'warning' });
            queryClient.invalidateQueries({ queryKey: ['availability', slug] });
            queryClient.invalidateQueries({ queryKey: ['tables', slug] });
          }}
          onBooked={(booking, token, remember) => {
            if (remember) {
              rememberBooking(slug, {
                reference: booking.reference,
                token,
                date: booking.date,
                time: booking.time,
                party_size: booking.party_size,
                table_ids: booking.tables.map((t) => t.id),
                table_labels: booking.tables.map((t) => t.label),
                venue_name: venue.name,
              });
            }
            setResult({ booking, token });
            setTableIds(booking.tables.map((t) => t.id));
            const firstPlan = booking.tables[0]?.floor_plan_id;
            if (firstPlan) setPlanId(firstPlan);
            setStage('done');
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }}
        />
      )}
      {stage === 'done' && result && <Confirmation result={result} venue={venue} today={data.today} tableById={tableById} activeRoute={activeRoute} setActiveRoute={setActiveRoute} />}
    </div>
  );

  const hasPhoto = plan?.assets.some((a) => a.kind === 'photo');
  const mapSection = plan && (
    <section className="bk-mapcol" aria-label="Floor plan">
      <div className="bk-maphead">
        {data.floor_plans.length > 1 ? (
          <div className="segmented" role="tablist">
            {data.floor_plans.map((p) => (
              <button key={p.id} type="button" aria-pressed={p.id === plan.id} onClick={() => setPlanId(p.id)}>
                {p.name}
              </button>
            ))}
          </div>
        ) : (
          <div className="bk-maptitle">{plan.name}</div>
        )}
        {hasPhoto && (
          <div className="segmented" aria-label="Map style">
            <button type="button" aria-pressed={!photo} onClick={() => setPhoto(false)}>
              Plan
            </button>
            <button type="button" aria-pressed={photo} onClick={() => setPhoto(true)}>
              Photo
            </button>
          </div>
        )}
      </div>
      <FloorMap
        key={plan.id}
        plan={plan}
        photo={photo}
        className="bk-map"
        tableClass={tableClass}
        selectedIds={tableIds}
        onTableClick={onTableClick}
        routesFor={routeTable && routeTable.wayfinding.length ? (plan.tables.some((t) => t.id === routeTable.id) ? routeTable : null) : null}
        activeRoute={activeRoute}
        ariaLabel={`${plan.name} floor plan`}
        overlay={
          !time && stage === 'choose' ? (
            <div className="fmap-hint">
              <Hand /> Choose a time to see which tables are free
            </div>
          ) : tablesQ.isFetching ? (
            <div className="fmap-hint">
              <Spinner /> Checking tables…
            </div>
          ) : null
        }
      />
      <div className="bk-legend" aria-hidden>
        <span><i className="lg lg-available" />Free</span>
        <span><i className="lg lg-selected" />Your table</span>
        <span><i className="lg lg-booked" />Taken</span>
        <span><i className="lg lg-other" />Not for {party}</span>
        {plan.backdrop === 'scan' && <span className="bk-scan-note">Map from a LIDAR scan · to scale</span>}
      </div>
    </section>
  );

  return (
    <div className={classNames('bk', embed && 'is-embed')} style={brandStyle}>
      <header className="bk-hero">
        <div className="bk-hero-inner">
          <div className="eyebrow bk-kind">
            {venue.kind}
            {venue.city ? ` · ${venue.city}` : ''}
          </div>
          <h1 className="display bk-name">{venue.name}</h1>
          <div className="bk-meta">
            {venue.address && (
              <span>
                <MapPin /> {venue.address}
              </span>
            )}
            {venue.phone && (
              <a href={`tel:${venue.phone.replace(/\s/g, '')}`}>
                <Phone /> {venue.phone}
              </a>
            )}
            <span>
              <Clock /> {data.hours_summary}
            </span>
          </div>
        </div>
      </header>
      <main className="bk-main">
        <div className="bk-side">
          {stage !== 'done' && top}
          {bottom}
          {!embed && (
            <footer className="bk-foot">
              {venue.guest_message && <p>{venue.guest_message}</p>}
              <p className="faint">
                Bookings by SpaceBooking · <Link to="/booking/find">Find a booking</Link>
              </p>
            </footer>
          )}
        </div>
        {mapSection}
      </main>
      {!isWide && stage === 'choose' && time && (
        <div className="bk-bar">
          <div className="grow">
            <div className="bk-bar-main">{summaryLine}</div>
            <div className="bk-bar-sub">{selectedTables.length ? `Table ${selectedTables.map((t) => t.label).join(' + ')}` : 'Best available table'}</div>
          </div>
          <button type="button" className="btn btn-primary" onClick={() => setStage('details')}>
            Continue
          </button>
        </div>
      )}
    </div>
  );
}

function reasonText(reason: string | null): string {
  switch (reason) {
    case 'full':
      return 'Fully booked';
    case 'pacing':
      return 'Kitchen is at capacity for this time';
    case 'too_soon':
      return 'Too soon to book online';
    case 'past':
      return 'This time has passed';
    case 'outside_window':
      return 'Not open for bookings yet';
    default:
      return 'Unavailable';
  }
}

function DetailsForm(props: {
  venue: PublicVenue;
  slug: string;
  date: string;
  time: string;
  party: number;
  tableIds: string[];
  remembered: ReturnType<typeof loadGuest>;
  summary: string;
  onBack: () => void;
  onConflict: (message: string) => void;
  onBooked: (booking: GuestBooking, token: string, remember: boolean) => void;
}) {
  const [name, setName] = useState(props.remembered?.name ?? '');
  const [phone, setPhone] = useState(props.remembered?.phone ?? '');
  const [email, setEmail] = useState(props.remembered?.email ?? '');
  const [occasion, setOccasion] = useState<string | null>(null);
  const [notes, setNotes] = useState('');
  const [marketing, setMarketing] = useState(false);
  const [remember, setRemember] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const formRef = useRef<HTMLFormElement>(null);

  useEffect(() => {
    formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!name.trim()) return setError('Please tell us your name.');
    if (phone.replace(/\D/g, '').length < 7) return setError('Please check your phone number — the venue may need to reach you.');
    setBusy(true);
    try {
      const res = await api<{ booking: GuestBooking; manage_token: string }>(`/api/public/venues/${props.slug}/bookings`, {
        method: 'POST',
        body: {
          date: props.date,
          time: props.time,
          party_size: props.party,
          table_ids: props.tableIds.length ? props.tableIds : null,
          name: name.trim(),
          phone: phone.trim(),
          email: email.trim() || null,
          occasion,
          notes: notes.trim() || null,
          marketing_opt_in: marketing,
        },
      });
      saveGuest(remember ? { name: name.trim(), phone: phone.trim(), email: email.trim() } : null);
      props.onBooked(res.booking, res.manage_token, remember);
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        const alts: string[] = err.details?.alternatives ?? [];
        props.onConflict(`${err.message}${alts.length ? ` Free nearby: ${alts.map(time12).join(', ')}.` : ''}`);
      } else setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="bk-step bk-details" onSubmit={submit} ref={formRef} noValidate>
      <div className="bk-details-head">
        <button type="button" className="btn btn-ghost btn-sm" onClick={props.onBack}>
          <ArrowLeft /> Back
        </button>
        <div className="bk-details-summary num">{props.summary}</div>
      </div>
      <h2 className="bk-step-title">Your details</h2>
      <div className="stack gap-4">
        <Field label="Name">{(id) => <input id={id} className="input" value={name} onChange={(e) => setName(e.target.value)} autoComplete="name" required />}</Field>
        <div className="bk-two">
          <Field label="Mobile" hint="Only used about this booking">
            {(id) => <input id={id} className="input" type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} autoComplete="tel" inputMode="tel" required />}
          </Field>
          <Field label="Email" optional hint="For your confirmation">
            {(id) => <input id={id} className="input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email" />}
          </Field>
        </div>
        <div className="field">
          <span className="field-label">
            Occasion <span className="optional">optional</span>
          </span>
          <div className="row wrap gap-2">
            {OCCASIONS.map((o) => (
              <button key={o.key} type="button" className="chip" aria-pressed={occasion === o.key} onClick={() => setOccasion(occasion === o.key ? null : o.key)}>
                {o.label}
              </button>
            ))}
          </div>
        </div>
        <Field label="Requests" optional hint="Allergies, a high chair, step-free access…">
          {(id) => <textarea id={id} className="textarea" rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} maxLength={1000} />}
        </Field>
        <label className="check">
          <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} />
          <span>Remember my details on this device</span>
        </label>
        <label className="check">
          <input type="checkbox" checked={marketing} onChange={(e) => setMarketing(e.target.checked)} />
          <span>Send me news and offers from {props.venue.name}</span>
        </label>
        {error && <Notice tone="danger">{error}</Notice>}
        <button type="submit" className="btn btn-primary btn-lg btn-block" disabled={busy}>
          {busy ? <Spinner /> : <Check />} Book table
        </button>
        <p className="bk-legal">
          {props.venue.name} keeps your details to manage this booking and to recognise you next time. You can cancel online up to{' '}
          {Math.round(props.venue.booking_rules.cancel_cutoff_min / 60)} hours before.
        </p>
      </div>
    </form>
  );
}

function Confirmation(props: {
  result: { booking: GuestBooking; token: string };
  venue: PublicVenue;
  today: string;
  tableById: Map<string, PlanTable>;
  activeRoute: string | null;
  setActiveRoute: (k: string | null) => void;
}) {
  const { booking, token } = props.result;
  const table = booking.tables.length === 1 ? props.tableById.get(booking.tables[0]!.id) : undefined;
  const pending = booking.status === 'pending';
  const manage = `/booking/${booking.reference}?token=${encodeURIComponent(token)}`;
  const entrance = table ? nearestByKind(table.wayfinding).find((w) => w.kind === 'entrance') : undefined;
  return (
    <section className="bk-done" aria-live="polite">
      <div className="bk-done-mark">
        <Check />
      </div>
      <h2 className="display bk-done-title">{pending ? 'Request received' : "You're booked"}</h2>
      <p className="muted">
        {pending ? `${props.venue.name} will confirm shortly.` : `See you ${relativeDay(booking.date, props.today).toLowerCase() === 'today' ? 'later today' : `on ${longDate(booking.date)}`}.`}
      </p>
      <div className="ticket">
        <div className="ticket-row">
          <div>
            <div className="eyebrow">When</div>
            <div className="ticket-big num">
              {shortDate(booking.date)}, {time12(booking.time)}
            </div>
          </div>
          <div>
            <div className="eyebrow">Guests</div>
            <div className="ticket-big num">{booking.party_size}</div>
          </div>
          <div>
            <div className="eyebrow">Table</div>
            <div className="ticket-big">{booking.tables.map((t) => t.label).join(' + ') || '—'}</div>
          </div>
        </div>
        <div className="ticket-ref">
          <span className="eyebrow">Reference</span>
          <span className="mono ticket-code">{spelledReference(booking.reference)}</span>
        </div>
      </div>
      {entrance && (
        <p className="bk-arrive">
          <Info /> Your table is a {metres(entrance.walk_m)} walk from the entrance.
        </p>
      )}
      {table && table.wayfinding.length > 0 && <WayfindingList table={table} active={props.activeRoute} onActive={props.setActiveRoute} compact />}
      <div className="row wrap gap-2" style={{ marginTop: 18 }}>
        <a className="btn" href={`/api/public/bookings/${booking.reference}/calendar.ics?token=${encodeURIComponent(token)}`}>
          <CalendarPlus /> Add to calendar
        </a>
        <Link className="btn" to={manage}>
          Change or cancel
        </Link>
        <button type="button" className="btn btn-ghost" onClick={() => window.location.reload()}>
          Book another
        </button>
      </div>
    </section>
  );
}
